import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoadingController, ModalController, Platform } from '@ionic/angular';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component';
import { TOAST_STATE, ToastService } from '../../../services/toast.service';
import { CheckDetailInterface, CheckViewInterface, EditorDataInterface, QuickComment, StationAudit, UserQuestionnaire, UserQuestion, PreviousCheckDTO } from '../../../models/check-interface';
import { CheckStorageService } from '../../../services/check-storage.service';
import { TranslateService } from "@ngx-translate/core";
import { map } from 'rxjs/operators';
import { PHBHPLayoutPackageService } from 'ph-bhp-feature-packages/ph-bhp-layout-package/public_api';
import { AppConfig } from 'src/app/app.config';
import { cloneWithOffset } from 'ngx-bootstrap/chronos/units/offset';
import * as bootstrap from 'bootstrap'; // Importa Bootstrap en tu componente
import { InactivityCheckModalComponent } from '../../modals/inactivity-check-modal/inactivity-check-modal.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'lib-hygiene-check-page',
  templateUrl: './hygiene-check-page.component.html',
  styleUrls: ['./hygiene-check-page.component.scss']
})
export class HygieneCheckPageComponent implements OnInit {

  public hygieneTabSelected = 0;
  public device = 'desktop';
  public checkId: any;
  public wardId: string;

  @ViewChild('inactivityModal') inactivityModal!: InactivityCheckModalComponent;
  @Input() id!: string;
  @Input() mode!: 'edit' | 'view';

  public userQuestions!: UserQuestion[];
  public userQuestionnaire!: UserQuestionnaire;
  public stationAudits: StationAudit[] = [];
  public isFirstSAMessageShown!: boolean;
  public load!: HTMLIonLoadingElement;
  public institutionName!: string;
  public wardName!: string;
  public departmentName!: string;
  public keyboardOpen: boolean = false;
  public quickComments: QuickComment[] = [];
  public inactivityInterval: any;
  public countdownInterval: any;
  public editorData: EditorDataInterface;
  public check: CheckDetailInterface;
  public previousCheck: PreviousCheckDTO;

  constructor(
    private router: Router,
    private modalCtrl: ModalController,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private dialog: MatDialog,
    private loadingCtrl: LoadingController,
    private config: AppConfig,
    public translateService: TranslateService,
    public checkStorageService: CheckStorageService,
    public platform: Platform,
    public layoutService: PHBHPLayoutPackageService
  ) {
    this.device = 'desktop';
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: Event) {
    if (this.mode === 'edit') {
      this.checkStorageService.putCheckEditedBy(this.checkId, -1).subscribe(() => { });
    }
  }

  ngOnDestroy() {
    if (this.mode === 'edit') {
      this.checkStorageService.putCheckEditedBy(this.checkId, -1).subscribe(() => { });
    }

    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
    }

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  isReportCheck: boolean = false;
  ngOnInit(): void {
    if (this.router.url.includes('view')) {
      this.mode = 'view';
    } else {
      this.mode = 'edit';
    }

    if (this.router.url.includes('checkIndividualData')) {
      this.isReportCheck = true;
    } else {
      this.isReportCheck = false;
    }

    this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
      this.loadData(params.get('id'));
    });

    this.checkStorageService.getQuickComments().pipe(
      map((response: any) =>
        response.map((item: any) => ({ ...item, selected: false }))
      )).subscribe({
        next: (quickComments: any) => {
          this.quickComments = quickComments;
        },
        error: (err: any) => {
          console.log('Error:', err);
        }
      });

    if (!this.inactivityInterval && this.mode === 'edit') {
      this.setupInactivityInterval();
    }
  }

  setupInactivityInterval() {
    this.inactivityInterval = setInterval(() => {
      this.checkStorageService.checkEditorIsInactive(this.checkId).subscribe((isInactive: boolean) => {
        if (isInactive) {
          clearInterval(this.inactivityInterval);
          this.inactivityModal.countdown = environment.inactivityModal.countdown;
          this.inactivityModal.showModal();
          this.countdownInterval = setInterval(() => {
            this.inactivityModal.countdown--;
            if (this.inactivityModal.countdown === 0) {
              clearInterval(this.countdownInterval);
              this.inactivityModal.closeModal();
              setTimeout(() => { this.returnOverview(); }, 1 * 250);
            }
          }, 1 * 1000);
        }
      });
    }, environment.inactivityModal.inactivityIntervalTime);
  }

  continueEditing() {
    clearInterval(this.countdownInterval);
    this.setupInactivityInterval();
    this.checkStorageService.putCheckEditedBy(this.checkId, this.config.userData.userId).subscribe(() => { });
  }

  returnOverview() {
    this.closePage();
  }

  public showButtons: boolean = true;

  loadData(id: any) {
    this.checkId = id;
    if (this.checkId) {
      if (this.mode === 'view') {
        this.layoutService.showLoader = true;
        this.checkStorageService.getViewCheckById(this.checkId).subscribe((check: CheckViewInterface) => {
          const userQuestionnaire = check['userQuestionnaire'];
          this.userQuestionnaire = check['userQuestionnaire'];
          this.userQuestions = [...userQuestionnaire['userQuestions']];
          this.stationAudits = check.stationAudits;
          this.wardId = check['wardId'] ? String(check['wardId']) : (localStorage.getItem('wardId') || '0');
          if (check.editedBy != -1) {
            this.editorData = {
              editedBy: check.editedBy,
              editedByUsername: check.editedByUsername,
            } as EditorDataInterface;
          }
          this.institutionName = check.institutionName || localStorage.getItem('institutionName') || '';
          this.wardName = check.wardName || localStorage.getItem('wardName') || '';
          this.departmentName = check.departmentName || localStorage.getItem('departmentName') || '';

          this.layoutService.showLoader = false;
        }, () => {
          this.layoutService.showLoader = false;
        });
      } else {
        this.layoutService.showLoader = true;
        this.checkStorageService.getChecksById(this.checkId).subscribe((check: CheckDetailInterface) => {
          this.check = check;
          this.config.checkId = this.checkId;
          const userQuestionnaire = check['userQuestionnaire'];
          this.userQuestionnaire = check['userQuestionnaire'];
          this.userQuestions = [...userQuestionnaire['userQuestions']];
          this.stationAudits = check.stationAudits;
          this.isFirstSAMessageShown = check['isFirstSAMessageShown'];
          this.wardId = check['wardId'] ? String(check['wardId']) : (localStorage.getItem('wardId') || '0');
          this.institutionName = check.institutionName || localStorage.getItem('institutionName') || '';
          this.wardName = check.wardName || localStorage.getItem('wardName') || '';
          this.departmentName = check.departmentName || localStorage.getItem('departmentName') || '';

          this.layoutService.showLoader = false;
        }, () => {
          this.layoutService.showLoader = false;
        });
      }
    }
  }

  saveChanges(event: boolean) {
    if (!event) {
      return;
    }

    this.layoutService.showLoader = true;
    this.checkStorageService.completeCheck(this.checkId).subscribe(() => {
      this.checkStorageService.putCheckUpdatedBy(this.config.checkId, this.config.userData.userId);
      this.layoutService.showLoader = false;
      this.router.navigateByUrl("hygiene-check-menu/hygiene-check");
    }, (err) => {
      this.toastService.showToast(TOAST_STATE.danger, 'Error', 'The request has failed. Please, try again.');
      this.layoutService.showError('The request has failed. Please, try again.', 'Error');
      this.layoutService.showLoader = false;
    });
  }

  async showLoading() {
    this.load = await this.loadingCtrl.create({
      message: 'Loading...',
      spinner: 'circular',
    });
    await this.load.present();
  }

  updateStationAudit() {
    this.loadData(this.checkId);
  }

  getPreviousCheck() {
    if (!this.previousCheck) {
      this.layoutService.showLoader = true;
      this.checkStorageService.getPreviousChecksByWards(this.wardId.toString()).subscribe((previousCheck: PreviousCheckDTO[]) => {
        this.layoutService.showLoader = false;
        if (previousCheck.length > 0) {
          previousCheck[0].previousStationAudits.sort((a: any, b: any) => a.orderNumber - b.orderNumber);
          this.previousCheck = previousCheck[0];
          this.hygieneTabSelected = 3
        } else {
          this.layoutService.showWarning(this.translateText('CHECK.NO_PREVIOUS_CHECKS'));
        }
      }, (err) => {
        this.layoutService.showLoader = false;
      });
    } else {
      this.hygieneTabSelected = 3
    }
  }

  closePage() {
    if (this.mode === 'edit') {
      this.checkStorageService.putCheckEditedBy(this.checkId, -1).subscribe(() => {
        this.router.navigateByUrl("hygiene-check-menu/hygiene-check");
      });
    } else {
      this.router.navigateByUrl("hygiene-check-menu/hygiene-check");
    }
    this.hygieneTabSelected = 0;
  }

  closeViewPage() {
    if (this.isReportCheck) {
      window.history.back();
    } else {
      this.router.navigateByUrl("hygiene-check-menu/hygiene-check");
    }
  }

  translateText(text: string) {
    let result: string = text;
    this.translateService.get(text).subscribe((res: string) => {
      result = res;
    });
    return result;
  }

  onClickStationAuditTab() {
    if (this.mode == "view" && this.stationAudits.length > 0) {
      this.hygieneTabSelected = 1;
    }
  }
}
